import { lazy, Suspense, useEffect } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { lazyRetry } from "@/lib/lazyRetry";
import { storeReferralCode } from "@/lib/referral";
import { trackEvent } from "@/lib/analytics";
import Home from "../pages/home/page";

// Todos os chunks de página usam lazyRetry(): falhas TRANSITÓRIAS de rede ao
// baixar o chunk são tentadas de novo sem recarregar a página (corrige os
// "Failed to fetch dynamically imported module" intermitentes). Se ainda falhar,
// o erro chega ao ErrorBoundary, que recarrega a página com o index.html novo.
const PacientePage = lazy(() => lazyRetry(() => import("../pages/paciente/page")));
const AcessarPage = lazy(() => lazyRetry(() => import("../pages/acessar/page")));
const DoctorLayout = lazy(() => lazyRetry(() => import("../pages/doctor/DoctorLayout")));
const DoctorPainelPage = lazy(() => lazyRetry(() => import("../pages/doctor/painel/page")));
const DoctorAgendaPage = lazy(() => lazyRetry(() => import("../pages/doctor/agenda/page")));
const DoctorPerfilPage = lazy(() => lazyRetry(() => import("../pages/doctor/perfil/page")));
const DoctorFinanceiroPage = lazy(() => lazyRetry(() => import("../pages/doctor/financeiro/page")));
const DoctorSeloPage = lazy(() => lazyRetry(() => import("../pages/doctor/badge/page")));
const PaymentPage = lazy(() => lazyRetry(() => import("../pages/payment/page")));
const AdminPage = lazy(() => lazyRetry(() => import("../pages/admin/page")));
const NutricionistaPage = lazy(() => lazyRetry(() => import("../pages/nutricionista/page")));
const MarketingAccessPage = lazy(() => lazyRetry(() => import("../pages/marketing/page")));
const IntelPage = lazy(() => lazyRetry(() => import("../pages/intel/page")));
const SuccessPage = lazy(() => lazyRetry(() => import("../pages/success/page")));
const InviteRegisterPage = lazy(() => lazyRetry(() => import("../pages/invite-register/page")));
const ValorDeMercadoPage = lazy(() => lazyRetry(() => import("../pages/valordemercado/page")));
const LaudoTecnicoPage = lazy(() => lazyRetry(() => import("../pages/laudo-tecnico/page")));
const EmagrecimentoPage = lazy(() => lazyRetry(() => import("../pages/emagrecimento/page")));
// Landing 2, feita para o Google Ads (sem menção a receita — ver comentário no topo da página).
const InicioPage = lazy(() => lazyRetry(() => import("../pages/inicio/page")));
const EmagrecimentoCheckoutPage = lazy(() => lazyRetry(() => import("../pages/emagrecimento/checkout")));
const ResetarSenhaPage = lazy(() => lazyRetry(() => import("../pages/resetar-senha/page")));
const DoctorSharePage = lazy(() => lazyRetry(() => import("../pages/doctor-share/page")));
const NotFound = lazy(() => lazyRetry(() => import("../pages/NotFound")));
const ObservarPage = lazy(() => lazyRetry(() => import("../pages/observar/page")));
const ConsultaPage = lazy(() => lazyRetry(() => import("../pages/consulta/page")));
const PrescricaoPage = lazy(() => lazyRetry(() => import("../pages/prescricao/page")));
const PrescricaoAvulsaPage = lazy(() => lazyRetry(() => import("../pages/prescricao-avulsa/page")));
const DocumentoAssinadoPage = lazy(() => lazyRetry(() => import("../pages/documento-assinado/page")));
const VerificarDocumentoPage = lazy(() => lazyRetry(() => import("../pages/verificar-documento/page")));
const SalaEsperaPage = lazy(() => lazyRetry(() => import("../pages/sala-espera/page")));
const ChatPage = lazy(() => lazyRetry(() => import("../pages/chat/page")));
const ProntuarioPage = lazy(() => lazyRetry(() => import("../pages/prontuario/page")));
const DoctorAjudaPage = lazy(() => lazyRetry(() => import("../pages/doctor/ajuda/page")));
const DoctorNotifPage = lazy(() => lazyRetry(() => import("../pages/doctor/notificacoes/page")));
const DoctorHorariosPage = lazy(() => lazyRetry(() => import("../pages/doctor/horarios/page")));
const DoctorAlterarSenhaPage = lazy(() => lazyRetry(() => import("../pages/doctor/alterar-senha/page")));
const MedicoCadastroPage = lazy(() => lazyRetry(() => import("../pages/doctor/cadastro/page")));
const MedicoCredenciamentoPage = lazy(() => lazyRetry(() => import("../pages/doctor/credenciamento/page")));
const MedicoCredObrigadoPage = lazy(() => lazyRetry(() => import("../pages/doctor/credenciamento/obrigado/page")));
const TermosVisualizadorPage = lazy(() => lazyRetry(() => import("../pages/termos/visualizador/page")));
// Portal do Paciente — todas as rotas vivem sob /paciente
const PatientReceitasPage = lazy(() => lazyRetry(() => import("../pages/paciente/receitas/page")));
const PatientEspecialidadePage = lazy(() => lazyRetry(() => import("../pages/paciente/especialidades/page")));
const PatientNotifPage = lazy(() => lazyRetry(() => import("../pages/paciente/notificacoes/page")));
const PatientAlterarSenhaPage = lazy(() => lazyRetry(() => import("../pages/paciente/alterar-senha/page")));
const PatientHistoricoPage = lazy(() => lazyRetry(() => import("../pages/paciente/historico/page")));
const PatientIndicacaoPage = lazy(() => lazyRetry(() => import("../pages/paciente/indicacao/page")));
const PatientMeusDadosPage = lazy(() => lazyRetry(() => import("../pages/paciente/meusdados/page")));
const PatientAjudaPage = lazy(() => lazyRetry(() => import("../pages/paciente/ajuda/page")));
// Blink+ (30/10/2026): área do assinante — 1ª fase só para pacientes de teste.
const PatientBlinkPlusPage = lazy(() => lazyRetry(() => import("../pages/paciente/blinkplus/page")));
// Funil de agendamento sob /paciente — /paciente/especialidade → /paciente/especialidade/medico → /paciente/especialidade/medico/horario
const AgendarPage = lazy(() => lazyRetry(() => import("../pages/agendar/page")));
const AgendarSpecialtyStep = lazy(() => lazyRetry(() => import("../pages/agendar/steps").then((m) => ({ default: m.AgendarSpecialtyStep }))));
const AgendarDoctorStep = lazy(() => lazyRetry(() => import("../pages/agendar/steps").then((m) => ({ default: m.AgendarDoctorStep }))));
const AgendarScheduleStep = lazy(() => lazyRetry(() => import("../pages/agendar/steps").then((m) => ({ default: m.AgendarScheduleStep }))));

// Preload map: path → import function
export const routePreloads: Record<string, () => Promise<any>> = {
  "/paciente": () => import("../pages/paciente/page"),
  "/paciente/receitas": () => import("../pages/paciente/receitas/page"),
  "/paciente/documentos": () => import("../pages/paciente/receitas/page"),
  "/paciente/especialidades": () => import("../pages/paciente/especialidades/page"),
  "/paciente/notificacoes": () => import("../pages/paciente/notificacoes/page"),
  "/paciente/alterar-senha": () => import("../pages/paciente/alterar-senha/page"),
  "/paciente/historico": () => import("../pages/paciente/historico/page"),
  "/paciente/meusdados": () => import("../pages/paciente/meusdados/page"),
  "/paciente/indicacao": () => import("../pages/paciente/indicacao/page"),
  "/paciente/ajuda": () => import("../pages/paciente/ajuda/page"),
  "/paciente/blinkplus": () => import("../pages/paciente/blinkplus/page"),
  "/paciente/especialidade": () => import("../pages/agendar/page"),
  "/paciente/especialidade/medico": () => import("../pages/agendar/page"),
  "/paciente/especialidade/medico/horario": () => import("../pages/agendar/page"),
  // Aliases legados (/consultar → /paciente): mantidos para links antigos com preload
  "/consultar": () => import("../pages/paciente/page"),
  "/consultar/receitas": () => import("../pages/paciente/receitas/page"),
  "/consultar/especialidades": () => import("../pages/paciente/especialidades/page"),
  "/consultar/notificacoes": () => import("../pages/paciente/notificacoes/page"),
  "/consultar/alterar-senha": () => import("../pages/paciente/alterar-senha/page"),
  "/consultar/historico": () => import("../pages/paciente/historico/page"),
  "/consultar/meusdados": () => import("../pages/paciente/meusdados/page"),
  "/consultar/indicacao": () => import("../pages/paciente/indicacao/page"),
  "/consultar/ajuda": () => import("../pages/paciente/ajuda/page"),
  "/consultar/especialidade": () => import("../pages/agendar/page"),
  "/consultar/especialidade/medico": () => import("../pages/agendar/page"),
  "/consultar/especialidade/medico/horario": () => import("../pages/agendar/page"),
  "/acessar": () => import("../pages/acessar/page"),
  "/checkout": () => import("../pages/payment/page"),
  "/obrigado": () => import("../pages/success/page"),
  "/admin": () => import("../pages/admin/page"),
  "/marketing": () => import("../pages/marketing/page"),
  "/nutricionista": () => import("../pages/nutricionista/page"),
  "/intel": () => import("../pages/intel/page"),
  "/medico": () => import("../pages/doctor/DoctorLayout"),
  "/medico/prontuario": () => import("../pages/prontuario/page"),
  "/medico/financeiro": () => import("../pages/doctor/financeiro/page"),
  "/medico/selo": () => import("../pages/doctor/badge/page"),
  "/medico/credenciamento/obrigado": () => import("../pages/doctor/credenciamento/obrigado/page"),
  "/cadastro-paciente": () => import("../pages/invite-register/page"),
  "/resetar-senha": () => import("../pages/resetar-senha/page"),
  "/d/:doctorId": () => import("../pages/doctor-share/page"),
};

function LazyWrap({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={null}
    >
      {children}
    </Suspense>
  );
}

// Redireciona preservando a query string (ex.: ?invite_doctor=... em links de
// convite antigos que apontavam para /consultar).
function RedirectKeepSearch({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search, hash: location.hash }} replace />;
}

// Redireciona /prontuario/:id (URL antiga) para a rota por paciente
// /medico/prontuario/:id — o prontuário agora é do PACIENTE, não da consulta.
function ProntuarioLegacyRedirect() {
  const { patientCaseId } = useParams();
  return <Navigate to={`/medico/prontuario/${patientCaseId}`} replace />;
}

// Link mágico de indicação (28/10/2026): /i/<código> guarda o código e abre o cadastro com ele já preenchido
// (10% na 1ª consulta). Ver lib/referral.ts.
function ReferralLinkRedirect() {
  const { code } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    const saved = storeReferralCode(code);
    if (saved) trackEvent("referral_link_open", "indicacao", { code: saved });
    navigate("/paciente?modo=cadastro", { replace: true });
  }, [code, navigate]);
  return null;
}

const routes: RouteObject[] = [
  { path: "/", element: <Home /> },
  {
    path: "/paciente",
    element: <LazyWrap><PacientePage /></LazyWrap>,
    // Rotas aninhadas do Portal do Paciente — o PacientePage (via
    // PatientPortalLayout) é o layout compartilhado: muda só o conteúdo central.
    children: [
      { path: "receitas", element: <LazyWrap><PatientReceitasPage /></LazyWrap> },
      // "Meus documentos" (29/10/2026): mesma página; é o destino do e-mail e do WhatsApp de documentos prontos.
      { path: "documentos", element: <LazyWrap><PatientReceitasPage /></LazyWrap> },
      { path: "especialidades", element: <LazyWrap><PatientEspecialidadePage /></LazyWrap> },
      { path: "notificacoes", element: <LazyWrap><PatientNotifPage /></LazyWrap> },
      { path: "alterar-senha", element: <LazyWrap><PatientAlterarSenhaPage /></LazyWrap> },
      { path: "historico", element: <LazyWrap><PatientHistoricoPage /></LazyWrap> },
      { path: "meusdados", element: <LazyWrap><PatientMeusDadosPage /></LazyWrap> },
      { path: "indicacao", element: <LazyWrap><PatientIndicacaoPage /></LazyWrap> },
      { path: "ajuda", element: <LazyWrap><PatientAjudaPage /></LazyWrap> },
      { path: "blinkplus/*", element: <LazyWrap><PatientBlinkPlusPage /></LazyWrap> },
    ],
  },
  {
    // Funil de agendamento sob /paciente: cada etapa é uma URL própria, o
    // layout mantém o estado compartilhado (especialidade/médico/horário)
    // entre as etapas. Fica na mesma página (portal do paciente), só cresce o path.
    path: "/paciente/especialidade",
    element: <LazyWrap><AgendarPage /></LazyWrap>,
    children: [
      { index: true, element: <LazyWrap><AgendarSpecialtyStep /></LazyWrap> },
      { path: "medico", element: <LazyWrap><AgendarDoctorStep /></LazyWrap> },
      { path: "medico/horario", element: <LazyWrap><AgendarScheduleStep /></LazyWrap> },
    ],
  },
  { path: "/acessar", element: <LazyWrap><AcessarPage /></LazyWrap> },
  { path: "/checkout", element: <LazyWrap><PaymentPage /></LazyWrap> },
  { path: "/obrigado", element: <LazyWrap><SuccessPage /></LazyWrap> },
  { path: "/admin", element: <LazyWrap><AdminPage /></LazyWrap> },
  { path: "/marketing", element: <LazyWrap><MarketingAccessPage /></LazyWrap> },
  { path: "/nutricionista", element: <LazyWrap><NutricionistaPage /></LazyWrap> },
  { path: "/intel", element: <LazyWrap><IntelPage /></LazyWrap> },
  {
    path: "/medico",
    element: <LazyWrap><DoctorLayout /></LazyWrap>,
    children: [
      { index: true, element: <LazyWrap><DoctorPainelPage /></LazyWrap> },
      { path: "agenda", element: <LazyWrap><DoctorAgendaPage /></LazyWrap> },
      { path: "horarios", element: <LazyWrap><DoctorHorariosPage /></LazyWrap> },
      { path: "perfil", element: <LazyWrap><DoctorPerfilPage /></LazyWrap> },
      // Assinatura digital (Prescrição teste): aba do Perfil com endereço próprio — volta do gov.br/VidaaS/BirdID e
      // botões "Configurar assinatura" caem aqui (28/10/2026).
      { path: "assinatura", element: <LazyWrap><DoctorPerfilPage /></LazyWrap> },
      { path: "financeiro", element: <LazyWrap><DoctorFinanceiroPage /></LazyWrap> },
      { path: "selo", element: <LazyWrap><DoctorSeloPage /></LazyWrap> },
      { path: "alterar-senha", element: <LazyWrap><DoctorAlterarSenhaPage /></LazyWrap> },
    ],
  },

  { path: "/medico/cadastro", element: <LazyWrap><MedicoCadastroPage /></LazyWrap> },
  { path: "/medico/credenciamento", element: <LazyWrap><MedicoCredenciamentoPage /></LazyWrap> },
  { path: "/medico/credenciamento/obrigado", element: <LazyWrap><MedicoCredObrigadoPage /></LazyWrap> },
  { path: "/termos/:tipo", element: <LazyWrap><TermosVisualizadorPage /></LazyWrap> },
  { path: "/cadastro-paciente/:token", element: <LazyWrap><InviteRegisterPage /></LazyWrap> },
  { path: "/valordemercado", element: <LazyWrap><ValorDeMercadoPage /></LazyWrap> },
  { path: "/laudo-tecnico", element: <LazyWrap><LaudoTecnicoPage /></LazyWrap> },
  { path: "/emagrecimento", element: <LazyWrap><EmagrecimentoPage /></LazyWrap> },
  { path: "/inicio", element: <LazyWrap><InicioPage /></LazyWrap> },
  { path: "/emagrecimento/checkout", element: <LazyWrap><EmagrecimentoCheckoutPage /></LazyWrap> },
  { path: "/observar/:appointmentId", element: <LazyWrap><ObservarPage /></LazyWrap> },
  { path: "/consulta/:appointmentId", element: <LazyWrap><ConsultaPage /></LazyWrap> },
  { path: "/consulta/:appointmentId/prescricao", element: <LazyWrap><PrescricaoPage /></LazyWrap> },
  { path: "/consulta/avulsa/prescricao", element: <LazyWrap><PrescricaoAvulsaPage /></LazyWrap> },
  { path: "/consulta/:appointmentId/documento", element: <LazyWrap><DocumentoAssinadoPage /></LazyWrap> },
  { path: "/v", element: <LazyWrap><VerificarDocumentoPage /></LazyWrap> },
  { path: "/v/:code", element: <LazyWrap><VerificarDocumentoPage /></LazyWrap> },
  { path: "/i/:code", element: <ReferralLinkRedirect /> },
  // A página "Sobre" não existe mais, mas ainda recebe visitas de links antigos (11 em 3 dias davam 404, 28/10/2026).
  { path: "/sobre", element: <Navigate to="/" replace /> },
  { path: "/sala-espera/:emergencyId", element: <LazyWrap><SalaEsperaPage /></LazyWrap> },
  { path: "/chat/:roomId", element: <LazyWrap><ChatPage /></LazyWrap> },
  { path: "/medico/prontuario/:patientId", element: <LazyWrap><ProntuarioPage /></LazyWrap> },
  // Alias legado — links antigos (/prontuario/:id) caem na mesma página por paciente.
  { path: "/prontuario/:patientCaseId", element: <ProntuarioLegacyRedirect /> },

  { path: "/medico/perfil/badge", element: <RedirectKeepSearch to="/medico/selo" /> },
  { path: "/medico/perfil/ajuda", element: <LazyWrap><DoctorAjudaPage /></LazyWrap> },
  { path: "/medico/notificacoes", element: <LazyWrap><DoctorNotifPage /></LazyWrap> },
  // Redireciona URLs antigas (/consultar) para a nova estrutura /paciente,
  // preservando query string (importante p/ links de convite e bookmarks antigos).
  { path: "/consultar", element: <RedirectKeepSearch to="/paciente" /> },
  { path: "/consultar/receitas", element: <RedirectKeepSearch to="/paciente/receitas" /> },
  { path: "/consultar/especialidades", element: <RedirectKeepSearch to="/paciente/especialidades" /> },
  { path: "/consultar/notificacoes", element: <RedirectKeepSearch to="/paciente/notificacoes" /> },
  { path: "/consultar/alterar-senha", element: <RedirectKeepSearch to="/paciente/alterar-senha" /> },
  { path: "/consultar/historico", element: <RedirectKeepSearch to="/paciente/historico" /> },
  { path: "/consultar/meusdados", element: <RedirectKeepSearch to="/paciente/meusdados" /> },
  { path: "/consultar/indicacao", element: <RedirectKeepSearch to="/paciente/indicacao" /> },
  { path: "/consultar/ajuda", element: <RedirectKeepSearch to="/paciente/ajuda" /> },
  { path: "/consultar/especialidade", element: <RedirectKeepSearch to="/paciente/especialidade" /> },
  { path: "/consultar/especialidade/medico", element: <RedirectKeepSearch to="/paciente/especialidade/medico" /> },
  { path: "/consultar/especialidade/medico/horario", element: <RedirectKeepSearch to="/paciente/especialidade/medico/horario" /> },
  { path: "/resetar-senha", element: <LazyWrap><ResetarSenhaPage /></LazyWrap> },
  // /d/:doctorId/:especialidade = link de UMA especialidade, com o preço dela (19/10/2026).
  { path: "/d/:doctorId/:especialidade?", element: <LazyWrap><DoctorSharePage /></LazyWrap> },
  { path: "*", element: <NotFound /> },
];

export default routes;
